/* ----------------------------------------------------------------------------
 * turnEntity — Phase B of the voice turn/audio redesign: ONE explicit
 * conversation-turn entity per docs/brain/product/voice-turn-audio-redesign-
 * scope-2026-07-27.md §3, consolidating (not reinventing) the turn-routing
 * state that used to live as independent parallel ref-flags on
 * useVoiceController.ts (clarifyCtxRef, publishConfirmCtxRef, handsFreeTurnRef,
 * hasWokenRef, closedAtRef). CLARIFY and PUBLISH_CONFIRM are SUB-STATES this
 * entity carries (`subState`), not siblings; classifyFinal() is the ONE
 * per-state routing dispatcher onFinal now defers to instead of an inline,
 * ordering-sensitive if-chain.
 *
 * Pure, DOM-free, browser-free, network-free (mirrors wakeMachine.ts's
 * stepWakeFlow design) so the whole routing decision is unit-testable under
 * Node --test with no react, no mic, no TTS. useVoiceController.ts is the
 * thin glue: it holds ONE ConversationTurn instance (in a ref, since its
 * fields mutate synchronously exactly like the refs it replaces) and calls
 * its methods instead of reading/writing five separate refs.
 *
 * SCOPE (Phase B, locked): fold clarifyCtxRef/publishConfirmCtxRef into ONE
 * `subState` field; give handleTurnCloseWake a SINGLE close() transition that
 * resets hasWoken/handsFreeTurn/subState/cooldown together; centralise
 * onFinal's ordering-sensitive guard chain into classifyFinal(). Explicitly
 * OUT of Phase B (deferred, not silently absorbed): useHandsFree.ts's
 * turnInFlightRef / clarifyAnswerActiveRef (a different consumer, the
 * WakeEffect/CaptureWindow layer across the bridge boundary — folding those in
 * would require threading this entity across the HandsFreeBridge interface, a
 * bigger architectural change than "consolidation" — see the Phase B build
 * note).
 * -------------------------------------------------------------------------- */

import type { BriefingModule } from './wakeMachine.ts';
import { isTurnClose, detectModuleAsk, isBriefingIntent } from './wakeMachine.ts';
import { finalTranscriptAction } from '../state/machine.ts';
import { POST_CLOSE_COOLDOWN_MS, POST_ANSWER_SCORE_GRACE_MS } from './handsFreeConfig.ts';
import type { PublishConfirmDraft } from '../actions/publishConfirmModel.ts';

/** CLARIFY sub-state: the offered module menu + re-ask counter + the ORIGINAL
 *  wake utterance (needed to re-issue the scoped assist call once an answer
 *  resolves). Replaces clarifyCtxRef's payload byte-for-byte. */
export interface ClarifySubState {
  kind: 'clarify';
  modules: BriefingModule[];
  reAskCount: number;
  utterance: string;
}

/** PUBLISH_CONFIRM sub-state: the prepared draft + re-ask counter. Replaces
 *  publishConfirmCtxRef's payload byte-for-byte. */
export interface PublishConfirmSubState {
  kind: 'publish_confirm';
  draft: PublishConfirmDraft;
  reAskCount: number;
}

/** A turn is EITHER in no sub-state (a plain command/follow-up turn) OR in
 *  EXACTLY ONE of these two closed-set sub-states — never both, never a pair
 *  of parallel flags that can independently drift out of sync (the defect
 *  class this replaces). */
export type TurnSubState = ClarifySubState | PublishConfirmSubState | null;

/** The per-state routing decision for one STT onFinal result. Centralises the
 *  ordering that used to be an inline if-chain inside the STT onFinal closure
 *  (voice-wave-4 D1/close-reask root-cause class) into ONE pure, testable
 *  dispatcher keyed FIRST on the turn's current sub-state, matching §3's
 *  "onFinal becomes per-state routing" requirement. The glue (onFinal) still
 *  owns epoch/staleness (an IO-timing concern, not turn-content) and the
 *  actual side effects (dispatch/speak/assist) — this function ONLY decides
 *  which handler the transcript belongs to. */
export type FinalRoute =
  | { kind: 'publish_confirm_answer' }
  | { kind: 'turn_close' }
  | { kind: 'clarify_answer' }
  | { kind: 'clarify_no_answer_fallback' }
  | { kind: 'fragment_reprompt' }
  | { kind: 'module_ask'; keys: string[] }
  | { kind: 'briefing' }
  | { kind: 'normal' };

/** ONE conversation-turn entity (§3): owns the sub-state that used to be two
 *  independently-mutated parallel refs, the hands-free-turn flag, the
 *  once-per-session "woken" flag, and the post-close cooldown stamp. All
 *  mutation is synchronous field assignment (mirrors the plain-ref style the
 *  rest of the file uses) — held in ONE useRef in useVoiceController.ts
 *  instead of five. */
export class ConversationTurn {
  /** CLARIFY / PUBLISH_CONFIRM sub-state, or null for a plain turn. */
  subState: TurnSubState = null;
  /** true for the WHOLE hands-free wake turn (initial command capture + any
   *  clarify follow-up leg); false for PTT and for a settled/closed turn. */
  handsFreeTurn = false;
  /** false until the FIRST wake greeting has actually played this session;
   *  thereafter a wake speaks the "anything else?" follow-up instead. Reset to
   *  false by close() so the NEXT wake (after the cooldown) greets fresh. */
  hasWoken = false;
  /** wall-clock ms of the last explicit turn close, or null before any close
   *  this session. Entity-owned per §3 ("the 9s post-close cooldown becomes
   *  an entity-owned property"). */
  closedAtMs: number | null = null;

  /** TRUE while Aura's own TTS audio is ACTUALLY playing -- set by
   *  beginAnswering()/endAnswering(), bound to the REAL onStart/onEnded
   *  playback callbacks (never a bare VoiceStatus flip). Phase C / scope doc
   *  §3: the wake reducer's re-arm gate reads this (via isAnsweringOrGrace),
   *  not VoiceStatus -- closes the self-trigger TTS-bleed class. */
  answering = false;
  /** wall-clock ms of the last endAnswering() call, or null before any
   *  answer has played this session. Feeds the post-playback grace window. */
  answeringEndedAtMs: number | null = null;

  /** Start a fresh hands-free wake turn: enables the briefing-intent trigger
   *  and drops any stale sub-state from a prior turn (IVG #2.3-DF3). Does NOT
   *  touch hasWoken/closedAtMs — those are session/cooldown concerns, not
   *  turn-start concerns. */
  startWakeTurn(): void {
    this.handsFreeTurn = true;
    this.subState = null;
  }

  /** A manual press-to-talk takes OWNERSHIP of the turn (IVG F3): PTT speech
   *  must never route into a pending hands-free clarify/publish-confirm
   *  sub-state or inherit hands-free routing. */
  resetForManualTurn(): void {
    this.subState = null;
    this.handsFreeTurn = false;
  }

  /** The clarify follow-up leg's capture failed to open (D1 fix): abandon the
   *  clarify sub-state and end the hands-free turn so the controller's
   *  full-briefing fallback is the ONLY thing that happens next. */
  abandonClarify(): void {
    this.subState = null;
    this.handsFreeTurn = false;
  }

  /** The wake turn settled normally (no clarify follow-up opened). */
  endWakeTurn(): void {
    this.handsFreeTurn = false;
  }

  setClarify(modules: BriefingModule[], utterance: string): void {
    this.subState = { kind: 'clarify', modules, reAskCount: 0, utterance };
  }

  setPublishConfirm(draft: PublishConfirmDraft): void {
    this.subState = { kind: 'publish_confirm', draft, reAskCount: 0 };
  }

  /** Bump the current sub-state's re-ask counter in place. Returns the NEW
   *  count (0 if there is no current sub-state — defensive; call sites only
   *  invoke this from within their own kind-gated branch). */
  incReask(): number {
    if (this.subState === null) return 0;
    this.subState.reAskCount += 1;
    return this.subState.reAskCount;
  }

  clearSubState(): void {
    this.subState = null;
  }

  /** Clear the sub-state ONLY if it is currently the given kind — preserves
   *  the exact pre-Phase-B behaviour of stop()/cancelToTalk(), which cleared
   *  publishConfirmCtxRef alone and deliberately left a hands-free clarify
   *  sub-state untouched (an existing, out-of-scope latent behaviour Phase B
   *  must not silently change — see the Phase B build note). */
  clearSubStateIfKind(kind: 'clarify' | 'publish_confirm'): void {
    if (this.subState?.kind === kind) this.subState = null;
  }

  markWoken(): void {
    this.hasWoken = true;
  }

  /** Aura's own TTS audio has ACTUALLY started playing -- call ONLY from a
   *  real playback-start callback (onStart), never from a request/dispatch
   *  alone (Phase C). */
  beginAnswering(): void {
    this.answering = true;
    this.answeringEndedAtMs = null;
  }

  /** Aura's own TTS audio has ACTUALLY finished -- call ONLY from a real
   *  playback-ended callback (AuraAudioPlayer's real 'ended' event /
   *  BufferSource onended via SegmentedNarrator's onEnded, never a coarse
   *  VoiceStatus flip). Stamps the moment so isAnsweringOrGrace() can hold
   *  the scorer off for a short grace window afterwards (Phase C). */
  endAnswering(nowMs: number): void {
    this.answering = false;
    this.answeringEndedAtMs = nowMs;
  }

  /** TRUE while audio is physically in flight OR within graceMs of it ending
   *  -- the wake reducer's ONE source of truth for "is it safe to re-arm
   *  scoring", replacing a bare VoiceStatus==='idle' read (Phase C / the
   *  self-trigger fix, scope doc §3: "IDLE(armed)/scoring-active is only
   *  entered from a confirmed ANSWERING->CLOSING transition"). */
  isAnsweringOrGrace(nowMs: number, graceMs: number = POST_ANSWER_SCORE_GRACE_MS): boolean {
    if (this.answering) return true;
    return this.answeringEndedAtMs !== null && nowMs - this.answeringEndedAtMs < graceMs;
  }

  /** TRUE while a wake fire is inside the post-close cooldown window — a
   *  marginal re-fire seconds after an explicit close, not a genuine new
   *  "Hey Aura" (D4 fix). */
  isInCooldown(nowMs: number): boolean {
    return this.closedAtMs !== null && nowMs - this.closedAtMs < POST_CLOSE_COOLDOWN_MS;
  }

  /** THE single CLOSING transition (§3 / Phase B item 2): resets EVERYTHING a
   *  turn-close owns in ONE place — hasWoken, handsFreeTurn, the sub-state,
   *  and the cooldown stamp — replacing wave-4's handleTurnCloseWake five-line
   *  manual reset. */
  close(nowMs: number): void {
    this.hasWoken = false;
    this.closedAtMs = nowMs;
    this.handsFreeTurn = false;
    this.subState = null;
  }

  /** THE per-state onFinal routing dispatcher (§3 / Phase B item 3). Pure:
   *  reads only `this.subState`/`this.handsFreeTurn` and the transcript.
   *  Byte-identical decision order to the pre-Phase-B inline chain — see the
   *  Phase B build note's behaviour-preservation table. */
  classifyFinal(transcript: string): FinalRoute {
    // Sub-state-first dispatch (§3 "per-state routing"): a PUBLISH_CONFIRM
    // sub-state ALWAYS wins (closed-set yes/edit/no answers are frequently
    // one word — must never be mistaken for a turn-close/fragment/command).
    if (this.subState?.kind === 'publish_confirm') return { kind: 'publish_confirm_answer' };

    // TURN-CLOSE: evaluated before the fragment guard (a close phrase is
    // often a single word) and NEVER while a CLARIFY sub-state is open (a
    // clarify answer is evaluated on its own terms below, even if it happens
    // to overlap the close vocabulary — unchanged from the pre-Phase-B rule).
    if (this.handsFreeTurn && this.subState?.kind !== 'clarify' && isTurnClose(transcript)) {
      return { kind: 'turn_close' };
    }

    // CLARIFY-ANSWER: a non-empty transcript while a CLARIFY sub-state is
    // open is routed to the matcher before the fragment guard (single-word
    // answers like "SEO"/"Sales"/"all" must not be rejected as fragments).
    if (this.subState?.kind === 'clarify' && transcript.trim() !== '') {
      return { kind: 'clarify_answer' };
    }

    // FRAGMENT / empty / low-quality transcript.
    if (finalTranscriptAction(transcript) === 'reprompt') {
      // A no-answer DURING a clarify must not exit silently — full-briefing
      // fallback (the entity's clarify no-answer policy), never a dead end.
      if (this.subState?.kind === 'clarify') return { kind: 'clarify_no_answer_fallback' };
      return { kind: 'fragment_reprompt' };
    }

    // Hands-free-only routing (PTT never reaches here with handsFreeTurn set).
    if (this.handsFreeTurn) {
      const moduleAsk = detectModuleAsk(transcript);
      if (moduleAsk) return { kind: 'module_ask', keys: moduleAsk };
      if (isBriefingIntent(transcript)) return { kind: 'briefing' };
    }

    return { kind: 'normal' };
  }
}
